import type { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import type { ColourKey, ColourKeyCategory } from "./colourKeys";
import { layerCountSubLine } from "./keyLayers";
import { EPD_QUALITY_COLOURS, EPD_QUALITY_SWATCH_CLASSES } from "./epdQuality";

export type MappingSource = "auto" | "person" | "unmapped";

// Worst first, Not mapped last: a type takes the earliest category any layer has, so an unmapped layer never hides an auto-mapped one.
export const MAPPING_SOURCE_WORST_FIRST: MappingSource[] = ["auto", "person", "unmapped"];

// Scene colours, mirroring the mapping-source-* tokens in style.css; hues no other key or the preview highlight uses.
export const MAPPING_SOURCE_COLOURS: Record<MappingSource, string> = {
  auto: "#f472b6",
  person: "#38bdf8",
  unmapped: EPD_QUALITY_COLOURS.unmapped,
};

export const MAPPING_SOURCE_SWATCH_CLASSES: Record<MappingSource, string> = {
  auto: "bg-mapping-source-auto",
  person: "bg-mapping-source-person",
  unmapped: EPD_QUALITY_SWATCH_CLASSES.unmapped,
};

export const MAPPING_SOURCE_LABELS: Record<MappingSource, string> = {
  auto: "Auto-mapped",
  person: "Mapped by a person",
  unmapped: "Not mapped",
};

// The API stores false for every human mapping path, so a mapped layer without the flag is a person's.
export function layerSource(layer: SpeckleMaterial): MappingSource {
  if (layer.mapping == null) return "unmapped";
  return layer.isAutoMapping === true ? "auto" : "person";
}

const MAPPING_SOURCE_CATEGORIES: ColourKeyCategory[] = MAPPING_SOURCE_WORST_FIRST.map((source) => ({
  id: source,
  label: MAPPING_SOURCE_LABELS[source],
  colour: MAPPING_SOURCE_COLOURS[source],
  swatchClass: MAPPING_SOURCE_SWATCH_CLASSES[source],
}));

const SUB_LINE_WORDING: Record<string, string> = {
  auto: "auto-mapped",
  person: "mapped by a person",
  unmapped: "mapped",
};

export function sourceSubLine(type: SpeckleMaterial, category: string): string {
  return layerCountSubLine(type, category, layerSource, SUB_LINE_WORDING);
}

export const MAPPING_SOURCE_KEY = {
  id: "mappingSource" as const,
  label: "Mapping source",
  group: "mapping",
  hint: "Auto-mapping is a guess until a person maps the type; a type with any auto-mapped layer shows as auto-mapped.",
  categoriesFor: () => MAPPING_SOURCE_CATEGORIES,
  trackCategory: "unmapped",
  headline: {
    goodCategories: ["person"],
    text: (good: number, total: number) => `${good} / ${total} elements mapped by a person`,
  },
  classifyLayer: layerSource,
  subLine: sourceSubLine,
} satisfies ColourKey;
